<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useGroupStore } from '../stores/groupStore';
import type { GroupSummary } from '../types';

const props = defineProps<{
  fileId: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const groupStore = useGroupStore();
const loading = ref(false);
const adding = ref<string | null>(null);
const removing = ref<string | null>(null);
const currentGroups = ref<Array<{ id: string; name: string; type: string }>>([]);

onMounted(async () => {
  if (groupStore.customGroups.length === 0) {
    await groupStore.loadGroups();
  }
  // Fetch groups for this specific file
  try {
    const res = await fetch(`/api/files/${encodeURIComponent(props.fileId)}/groups`);
    if (res.ok) {
      const data = await res.json();
      currentGroups.value = data.groups || [];
    }
  } catch {
    // ignore
  }
});

function isChecked(groupId: string): boolean {
  return !!currentGroups.value.some((g) => g.id === groupId);
}

async function toggleGroup(groupId: string) {
  if (isChecked(groupId)) {
    // Remove from group
    removing.value = groupId;
    try {
      await groupStore.removeVideoFromGroup(groupId, props.fileId);
      // Refresh current groups
      const res = await fetch(`/api/files/${encodeURIComponent(props.fileId)}/groups`);
      if (res.ok) {
        const data = await res.json();
        currentGroups.value = data.groups || [];
      }
    } catch (e: unknown) {
      alert('移除失败: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      removing.value = null;
    }
  } else {
    // Add to group
    adding.value = groupId;
    try {
      await groupStore.addVideoToGroup(groupId, props.fileId);
      // Refresh current groups
      const res = await fetch(`/api/files/${encodeURIComponent(props.fileId)}/groups`);
      if (res.ok) {
        const data = await res.json();
        currentGroups.value = data.groups || [];
      }
    } catch (e: unknown) {
      alert('添加失败: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      adding.value = null;
    }
  }
}

function formatDate(dateStr: string): string {
  return dateStr.slice(0, 10);
}
</script>

<template>
  <div class="modal-backdrop" @click="emit('close')">
    <div class="modal-dialog" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">管理分组</h3>
        <button class="close-btn" @click="emit('close')">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <!-- 自定义分组 -->
      <div class="section">
        <h4 class="section-label">自定义分组</h4>
        <div v-if="groupStore.customGroups.length === 0" class="empty-hint">
          暂无自定义分组，请先创建
        </div>
        <label
          v-for="g in groupStore.customGroups"
          :key="g.id"
          class="group-option"
          :class="{ checked: isChecked(g.id) }"
        >
          <input
            type="checkbox"
            :checked="isChecked(g.id)"
            @change="toggleGroup(g.id)"
            :disabled="!!adding || !!removing"
          />
          <span class="option-name">{{ g.name }}</span>
          <span class="option-count">{{ g.videoCount }} 个视频</span>
          <span v-if="adding === g.id" class="loading-spinner">+</span>
          <span v-if="removing === g.id" class="loading-spinner">-</span>
        </label>
      </div>

      <!-- 自动分组（只读） -->
      <div class="section">
        <h4 class="section-label">自动分组</h4>
        <div class="auto-group-list">
          <div v-if="groupStore.dateGroups.length === 0 && groupStore.typeGroups.length === 0" class="empty-hint">
            暂无自动分组
          </div>
          <div
            v-for="g in [...groupStore.dateGroups, ...groupStore.typeGroups]"
            :key="g.id + g.type"
            class="auto-group-item"
          >
            <span class="auto-group-name">{{ g.name }}</span>
            <span class="auto-group-type">{{ g.type === 'date' ? '日期' : '类型' }}</span>
            <span class="auto-group-count">{{ g.videoCount }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease-out;
}

.modal-dialog {
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: 24px;
  width: 440px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  animation: fadeInUp 0.2s ease-out;
  box-shadow: var(--shadow-elevated);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all var(--transition-fast);
  display: flex;
}
.close-btn:hover {
  color: var(--text-primary);
}

.section {
  margin-bottom: 20px;
}

.section:last-child {
  margin-bottom: 0;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.group-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-bottom: 4px;
  border: 1px solid transparent;
}
.group-option:hover {
  background: var(--bg-secondary);
}
.group-option.checked {
  background: var(--accent-soft);
  border-color: var(--accent);
}

.group-option input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--accent);
  cursor: pointer;
  flex-shrink: 0;
}

.option-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  min-width: 0;
}

.option-count {
  font-size: 12px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.loading-spinner {
  font-size: 14px;
  font-weight: 700;
  color: var(--accent);
  flex-shrink: 0;
  width: 16px;
  text-align: center;
}

.auto-group-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.auto-group-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
  font-size: 13px;
}

.auto-group-name {
  flex: 1;
  font-weight: 500;
  min-width: 0;
}

.auto-group-type {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  flex-shrink: 0;
}

.auto-group-count {
  font-size: 12px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.empty-hint {
  color: var(--text-muted);
  font-size: 13px;
  padding: 8px 0;
}

@media (max-width: 768px) {
  .modal-dialog {
    padding: 16px;
    width: 320px;
  }
}
</style>
